import {
  type Currency,
  formatCurrency,
  isCurrentPlan,
  isNextPlan,
  type SubscriptionPlanConfig,
} from '@gofranz/common';
import { showApiErrorNotification } from '@gofranz/common-components';
import {
  Badge,
  Box,
  type BoxProps,
  Button,
  Card,
  Group,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCheck } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useRustyState } from '../../state';

interface SubscriptionPlansProps extends BoxProps {
  mode?: 'public' | 'private';
}

const NAVY = '#0A192F';

const PLAN_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: 24,
  alignItems: 'stretch',
} as const;

const SERVICE_LABELS: Record<string, { singular: string; plural: string }> = {
  message: { singular: 'message', plural: 'messages' },
  filter_spam: { singular: 'spam filter', plural: 'spam filters' },
  email_notification: { singular: 'email notification', plural: 'email notifications' },
  email_responder: { singular: 'email responder', plural: 'email responders' },
  integration: { singular: 'integration', plural: 'integrations' },
};

function formatAllowance(resource: string, amount: number) {
  if (amount === -1) return 'Unlimited';
  if (resource === 'file_upload') {
    const mb = amount / 1024;
    return `${mb.toLocaleString('en-US').replace(/,/g, ' ')} MB`;
  }
  return amount.toLocaleString('en-US').replace(/,/g, ' ');
}

function formatServiceLabel(service: string, amount: number) {
  const labels = SERVICE_LABELS[service];
  const isPlural = amount === -1 || amount > 1;
  if (labels) return isPlural ? labels.plural : labels.singular;
  return service.replace(/_/g, ' ');
}

function formatFeature(feature: string) {
  if (feature.startsWith('integration:')) {
    return feature
      .split(':')[1]
      .split(/[_-]/)
      .map((w) => (w.toLowerCase() === 'api' ? 'API' : w.charAt(0).toUpperCase() + w.slice(1)))
      .join(' ');
  }
  return feature.charAt(0).toUpperCase() + feature.slice(1);
}

function formatRetention(days: number | null | undefined) {
  if (days === null || days === undefined) return 'Unlimited';
  if (days >= 365) {
    const years = Math.round(days / 365);
    return `${years} year${years > 1 ? 's' : ''}`;
  }
  return `${days} days`;
}

interface CheckItemProps {
  children: React.ReactNode;
  highlight?: boolean;
}

function CheckItem({ children, highlight }: CheckItemProps) {
  return (
    <Group gap="xs" wrap="nowrap" align="flex-start">
      <ThemeIcon
        size={18}
        radius="xl"
        variant="light"
        color={highlight ? 'dark' : 'gray'}
        style={{ marginTop: 2, flexShrink: 0 }}
      >
        <IconCheck size={12} />
      </ThemeIcon>
      <Text size="sm" lh={1.5}>
        {children}
      </Text>
    </Group>
  );
}

export function SubscriptionPlans({ mode = 'private', ...boxProps }: SubscriptionPlansProps) {
  const {
    subscriptionPlans,
    currentSubscription,
    getAndSetSubscriptionPlans,
    getAndSetCurrentSubscription,
    subscribeToplan,
  } = useRustyState();

  const [loading, setLoading] = useState(false);
  const [subscribingToPlan, setSubscribingToPlan] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await getAndSetSubscriptionPlans();
        if (mode === 'private') {
          await getAndSetCurrentSubscription();
        }
      } catch (error) {
        console.error('Failed to load subscription data:', error);
        showApiErrorNotification(error, notifications, 'Loading Error');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [mode]);

  const handleSubscribe = async (planId: string) => {
    try {
      setSubscribingToPlan(planId);
      await subscribeToplan(planId);
    } catch (error: any) {
      console.error('Failed to subscribe:', error);
      showApiErrorNotification(error, notifications, 'Subscription Error');
    } finally {
      setSubscribingToPlan(null);
    }
  };

  if (loading || !subscriptionPlans) {
    return (
      <Box style={PLAN_GRID_STYLE} {...boxProps}>
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index} withBorder radius="md" p="xl">
            <Stack gap="md">
              <Skeleton height={24} width={100} />
              <Skeleton height={40} width={120} />
              <Skeleton height={14} width="100%" />
              <Skeleton height={14} width="80%" />
              <Skeleton height={14} width="90%" />
              <Skeleton height={36} width="100%" />
            </Stack>
          </Card>
        ))}
      </Box>
    );
  }

  const plans = subscriptionPlans;
  // Highlight middle plan (or 3rd plan if 4+) as "Most popular" in public mode only
  const popularIndex = mode === 'public' && plans.length >= 3 ? (plans.length === 3 ? 1 : 2) : -1;

  return (
    <Box style={PLAN_GRID_STYLE} {...boxProps}>
      {plans.map((plan: SubscriptionPlanConfig, index: number) => {
        const isCurrent = isCurrentPlan(plan.id, currentSubscription);
        const isUpcoming = isNextPlan(plan.id, currentSubscription);
        const isPopular = index === popularIndex;

        const cardBorder =
          isCurrent || isUpcoming
            ? `2px solid ${NAVY}`
            : isPopular
              ? `1.5px solid ${NAVY}`
              : undefined;

        return (
          <Card
            key={plan.id}
            withBorder
            radius="md"
            p="xl"
            style={{
              position: 'relative',
              border: cardBorder,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible',
              marginTop: isPopular ? 12 : 0,
            }}
          >
            {isPopular && (
              <Box
                style={{
                  position: 'absolute',
                  top: -14,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#FFB800',
                  color: NAVY,
                  border: `1.5px solid ${NAVY}`,
                  borderRadius: 9999,
                  padding: '4px 14px',
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                  whiteSpace: 'nowrap',
                  zIndex: 1,
                }}
              >
                Most popular
              </Box>
            )}

            <Stack gap="md" style={{ flexGrow: 1 }}>
              <Group gap="xs">
                <Text fw={600} size="lg">
                  {plan.name}
                </Text>
                {mode === 'private' && isCurrent && (
                  <Badge size="xs" color="gray" variant="light">
                    Current
                  </Badge>
                )}
                {mode === 'private' && isUpcoming && (
                  <Badge size="xs" color="blue" variant="light">
                    Upcoming
                  </Badge>
                )}
              </Group>

              <Group gap={4} align="baseline">
                <Text size="2.25rem" fw={700} lh={1}>
                  {plan.price === 0
                    ? 'Free'
                    : formatCurrency([plan.currency as Currency, plan.price])}
                </Text>
                {plan.price > 0 && (
                  <Text size="sm" c="dimmed">
                    /month
                  </Text>
                )}
              </Group>

              <Stack gap="md" style={{ flexGrow: 1 }}>
                <Stack gap={6}>
                  <Text
                    size="xs"
                    fw={700}
                    tt="uppercase"
                    c="dimmed"
                    style={{ letterSpacing: '0.04em' }}
                  >
                    What's included
                  </Text>
                  {plan.allowances.map((allowance, i) => (
                    <CheckItem key={i} highlight={isPopular}>
                      <Text component="span" fw={500}>
                        {formatAllowance(allowance.service, allowance.limit)}
                      </Text>{' '}
                      <Text component="span" c="dimmed">
                        {formatServiceLabel(allowance.service, allowance.limit)}
                      </Text>
                    </CheckItem>
                  ))}
                </Stack>

                <Stack gap={6}>
                  <Text
                    size="xs"
                    fw={700}
                    tt="uppercase"
                    c="dimmed"
                    style={{ letterSpacing: '0.04em' }}
                  >
                    Data retention
                  </Text>
                  <CheckItem highlight={isPopular}>
                    <Text component="span" fw={500}>
                      {formatRetention(plan.max_retention_days)}
                    </Text>
                  </CheckItem>
                </Stack>

                {plan.features && plan.features.length > 0 && (
                  <Stack gap={6}>
                    <Text
                      size="xs"
                      fw={700}
                      tt="uppercase"
                      c="dimmed"
                      style={{ letterSpacing: '0.04em' }}
                    >
                      Integrations
                    </Text>
                    {plan.features.map((feature, i) => (
                      <CheckItem key={`feat-${i}`} highlight={isPopular}>
                        {formatFeature(feature)}
                      </CheckItem>
                    ))}
                  </Stack>
                )}
              </Stack>

              {mode === 'private' && (
                <Button
                  fullWidth
                  size="md"
                  radius="md"
                  variant={isCurrent || isUpcoming ? 'light' : isPopular ? 'filled' : 'outline'}
                  color={isCurrent || isUpcoming ? 'gray' : undefined}
                  styles={
                    isPopular && !isCurrent && !isUpcoming
                      ? {
                          root: {
                            backgroundColor: '#FFB800',
                            color: NAVY,
                          },
                        }
                      : undefined
                  }
                  disabled={isCurrent || isUpcoming || subscribingToPlan === plan.id}
                  loading={subscribingToPlan === plan.id}
                  onClick={() => handleSubscribe(plan.id)}
                >
                  {isCurrent
                    ? 'Current Plan'
                    : isUpcoming
                      ? 'Upcoming Plan'
                      : currentSubscription?.plan_id
                        ? 'Switch Plan'
                        : 'Get Started'}
                </Button>
              )}
            </Stack>
          </Card>
        );
      })}
    </Box>
  );
}
